export const endpoint = {
  platformAnnouncements: {
    active: "/platform-announcements/active",
    dismiss: (id: string) => `/platform-announcements/${id}/dismiss`,
  },
  auth: {
    postSignin: "/auth/login",
    postRegister: "/auth/register",
    checkEmailDomain: (email: string) => `/auth/check-email-domain?email=${encodeURIComponent(email)}`,
    postSignupFunnelLog: "/auth/signup-funnel-log",
    postLogout: "/auth/logout",
    postResend2fa: "/auth/2fa/resend",
    postVerify2fa: "/auth/2fa/verify",
    postAgencyToken: "/auth/agency/token",
    // Legacy path for user-agencies listing (fallback)
    getAgencies: "/auth/agency/list",
    postRequestPasswordReset: "/auth/request-reset-password",
    postResetPassword: "/auth/reset-password",
    postRefreshToken: "/auth/refresh",
    postGoogleLogin: "/auth/google",
    postAppleLogin: "/auth/apple",
    postMicrosoftLogin: "/auth/microsoft",
    postVerifyEmail: "/auth/verify-email",
    postVerifyPhone: "/auth/verify-phone",
    postChangePassword: "/auth/change-password",
    passkeyCheckPassword: "/auth/passkeys/check-password",
    passkeyConfirm: "/auth/passkeys/confirm",
    passkeysConfirmed: "/auth/passkeys/confirmed",
    postEnableMfa: "/auth/mfa/enable",
    postDisableMfa: "/auth/mfa/disable",
    getProfile: "/auth/profile",
    getStatus: "/auth/profile", // Returns user profile with accountStatus
    patchProfile: "/auth/profile",
    postLogoutAll: "/auth/logout-all",
    // MFA Setup endpoints (unified)
    mfaSetup: "/auth/mfa/setup",
    mfaVerifySetup: "/auth/mfa/verify-setup",
    setupMFA: "/auth/mfa/setup",
    verifyMFA: "/auth/mfa/verify-setup",
    disableMFA: "/auth/mfa/disable",
    setPreferredMFA: "/auth/mfa/set-preferred",
    getMFAStatus: "/auth/mfa/status",
    // MFA prompt tracking (for setup reminder banner)
    mfaPromptIncrement: "/auth/mfa/prompt/increment",
    mfaPromptDismiss: "/auth/mfa/prompt/dismiss",
    mfaPromptReset: "/auth/mfa/prompt/reset",
    // Security & Activity
    getLoginActivity: "/auth/login-activity",
    signOutAllDevices: "/auth/logout-all-devices",
    signOutSession: (sessionId: string) => `/auth/sessions/${sessionId}`,
    // Approval status (backend prefers query form, keeps path alias)
    approvalStatus: (userId: string) => `/auth/approval-status?userId=${encodeURIComponent(userId)}`,
    // Legacy endpoints (kept for backward compatibility but not used in new flow)
    mfaTotpSetup: "/auth/mfa/totp/setup",
    mfaTotpEnable: "/auth/mfa/totp/enable",
    mfaSmsToggle: "/auth/mfa/sms/toggle",
    mfaStatus: "/auth/mfa/status",
  },

  user: {
    getUsers: "/users",
    postCreateUsers: "/users",
    postCreateUsersInAgency: "/users/create-user-in-agency",
    putUpdateUser: (userId: string) => `/users/${userId}`,
    deleteUser: (userId: string) => `/users/${userId}`,
    getUserProfile: "/users/profile",
    getTrialStatus: "/users/account-management/trial-status",
    getProfile: "/users/profile",
    patchUpdateProfile: (userId: string) => `/users/profile/${userId}`,
    getUserTypes: "/users/types", // Get available user types from backend
    // New endpoints for user management
    becomeAgencyOwner: "/users/become-agency-owner",
    profileWithAgency: "/users/profile-with-agency",
    upgradeToFullFledged: "/users/upgrade-to-full-fledged",
    requestDeactivation: "/users/me/request-deactivation",
  },

  notifications: {
    getNotifications: "/notifications/my",
    getNotificationById: (id: string) => `/notifications/my/${id}`,
    markAsRead: (id: string) => `/notifications/my/${id}/read`,
    dismissNotification: (id: string) => `/notifications/my/${id}`,
    dismissAllNotifications: "/notifications/my/all",
    updateNotificationPreferences: "/notifications/preferences", // Fixed: was /users/profile/notification-preferences
    createNotification: "/notifications",
    updateNotification: (id: string) => `/notifications/${id}`,
    deleteNotification: (id: string) => `/notifications/${id}`,
    markAllAsRead: "/notifications/mark-all-read",
    archiveAllRead: "/notifications/archive-all-read",
    deleteAllArchived: "/notifications/delete-all-archived",
    bulkUpdate: "/notifications/bulk-update",
    getStats: "/notifications/stats",
    getPreferences: "/notifications/preferences",
    updatePreferences: "/notifications/preferences",
    realTimeStream: "/realtime/stream",
  },

  appointments: {
    getAppointments: "/appointments",
    getAppointmentsStats: "/appointments/stats/summary",
    getTodaysAppointments: "/appointments/today",
    getAgencyCalendar: "/appointments/agency-calendar",           // GET - owner/admin agency-wide calendar
    assignAppointment: (appointmentId: string) =>                 // PATCH - tag an agent
      `/appointments/${appointmentId}/assign`,
    getAppointmentById: (appointmentId: string) =>
      `/appointments/${appointmentId}`,
  },

  blockedNumbers: {
    list: "/blocked-numbers",                              // GET  — my blocked numbers
    block: "/blocked-numbers",                             // POST — { phone, leadId?, reason? }
    unblock: (id: string) => `/blocked-numbers/${id}`,     // DELETE
  },
  leads: {
    getLeads: "/leads",                        // GET /leads - List all leads
    leadTypeahead: "/leads/search/typeahead",  // GET - lightweight navbar search
    createLead: "/leads",                       // POST /leads - Create new lead
    getLeadById: (leadId: string) => `/leads/${leadId}`,  // GET /leads/:id - Get lead by ID
    updateLead: (leadId: string) => `/leads/${leadId}`,   // PATCH /leads/:id - Update lead
    deleteLead: (leadId: string) => `/leads/${leadId}`,   // DELETE /leads/:id - Delete lead
    bulkDeleteLeads: "/leads/bulk-delete",               // POST /leads/bulk-delete - Bulk delete leads
    bulkUpdateLeadStatus: "/leads/bulk-update-status",   // POST /leads/bulk-update-status - Bulk update lead status

    // Pipeline stages
    getPipelineStages: "/leads/pipeline-stages",                              // GET - visible stages
    getAllPipelineStages: "/leads/pipeline-stages/all",                       // GET - all stages (admin)
    createPipelineStage: "/leads/pipeline-stages",                            // POST - create custom stage
    updatePipelineStage: (stageId: string) => `/leads/pipeline-stages/${stageId}`,     // PATCH
    togglePipelineStageVisibility: (stageId: string) => `/leads/pipeline-stages/${stageId}/visibility`, // PATCH
    deletePipelineStage: (stageId: string) => `/leads/pipeline-stages/${stageId}`,     // DELETE
    reorderPipelineStages: "/leads/pipeline-stages/reorder",                  // POST
    resetPipelineStageOrder: "/leads/pipeline-stages/reset-order",            // POST
    getDeletedPipelineStages: "/leads/pipeline-stages/deleted",               // GET - soft-deleted custom stages
    pipelineStageImpact: (stageId: string) => `/leads/pipeline-stages/${stageId}/impact`,   // GET - lead counts before delete
    restorePipelineStage: (stageId: string) => `/leads/pipeline-stages/${stageId}/restore`, // POST - un-delete
    getLeadAudits: (leadId: string) => `/leads/${leadId}/audits`, // GET /leads/:id/audits - Get lead audit history
    getLeadsStats: "/leads/stats",              // GET /leads/stats - Lead statistics
    getCustomFieldKeys: "/leads/custom-field-keys", // GET - custom-field headers (with type) for the sort
    getCustomFieldValues: "/leads/custom-field-values", // GET ?key= - distinct values of a custom field for filtering
    bulkCreateLeads: "/leads/bulk",             // POST /leads/bulk - Bulk create leads
    uploadLeads: "/leads/import",               // POST /leads/import - Import leads
    exportLeads: "/leads/export",               // GET /leads/export - Export leads
    
    // Additional existing endpoints
    getLeadsWithPolicies: "/leads/with-policies",
    getLeadWithPolicy: (leadId: string) => `/leads/${leadId}/with-policies`,
    searchLeads: "/leads/search",
    permanentlyDeleteLead: (leadId: string) => `/leads/${leadId}/permanent`,
    permanentlyDeleteAllLeads: "/leads/permanent/all",
    recentlyDeletedMatches: "/leads/recently-deleted/matches",
    restoreDeletedLead: (leadId: string) => `/leads/${leadId}/restore`,
    restoreAllDeletedLeads: "/leads/restore-all",
    getDeletedLeads: "/leads/deleted",
    generateQuotes: (id: string) => `/leads/${id}/quotes`,
    getQuotes: (id: string) => `/leads/${id}/quotes`,
    saveSelectedQuotes: (id: string) => `/leads/${id}/quotes/select`,
    exportQuotesToPDF: (id: string) => `/leads/${id}/quotes/export-pdf`, // POST - Export quotes to PDF
    sendQuotesByEmail: (id: string) => `/leads/${id}/quotes/send-email`,
    testCompulifeConnection: "/leads/test/compulife",
    
    // Lead Notes endpoints
    getLeadNotes: (leadId: string) => `/leads/${leadId}/notes`,
    createLeadNote: (leadId: string) => `/leads/${leadId}/notes`,
    updateLeadNote: (leadId: string, noteId: string) => `/leads/${leadId}/notes/${noteId}`,
    deleteLeadNote: (leadId: string, noteId: string) => `/leads/${leadId}/notes/${noteId}`,
    revealLeadNote: (leadId: string, noteId: string) => `/leads/${leadId}/notes/${noteId}/reveal`,
    uploadLeadNoteImage: (leadId: string) => `/leads/${leadId}/notes/media`,

    // Lead Documents endpoints - Fixed to match backend routes
    uploadLeadDocuments: (leadId: string) => `/lead-documents/${leadId}/upload`,
    getLeadDocuments: (leadId: string) => `/lead-documents/${leadId}`,
    deleteLeadDocument: (leadId: string, documentId: string) => `/lead-documents/${leadId}/document?documentId=${documentId}`,
    initiateLeadDocumentDownload: (leadId: string, documentId: string) => `/lead-documents/${leadId}/download/initiate?documentId=${documentId}`,
    verifyLeadDocumentDownload: (leadId: string, documentId: string) => `/lead-documents/${leadId}/download/verify?documentId=${documentId}`,
    getDocumentDownloadUrl: () => `/lead-documents/download-url`,

    // Lead Import and Export endpoints
    getLeadsImportList: "/leads/imports",
    getLeadsImportById: (importId: string) => `/leads/imports/${importId}`,
    getImportFailedResults: (importId: string, limit = 100) =>
      `/leads/imports/${importId}/failed-results?limit=${limit}`,
    activateLeadsImportById: (importId: string) =>
      `/leads/imports/${importId}/bulk-activate`,
    activateSingleLeadFromImport: (importId: string, resultId: string) =>
      `/leads/imports/${importId}/activate/${resultId}`,
    getImportDeletionPreview: (importId: string) =>
      `/leads/imports/${importId}/deletion-preview`,
    deleteImport: (importId: string) => `/leads/imports/${importId}`,
    getDeletedImports: "/leads/imports/deleted", // GET - soft-deleted lists (Recently Deleted)
    restoreImport: (importId: string) => `/leads/imports/${importId}/restore`, // POST - undo a list delete
    bulkDeleteImports: "/leads/imports/bulk-delete", // POST - start a background bulk delete
    getBulkDeleteJobs: "/leads/imports/bulk-delete/jobs", // GET - recent bulk-delete jobs
    getBulkDeleteJob: (jobId: string) => `/leads/imports/bulk-delete/jobs/${jobId}`, // GET - one job's progress
    moveImportToStage: (importId: string) => `/leads/imports/${importId}/move-to-stage`,
    getStatusesSimple: "/leads/statuses/simple",
    getStatusesAvailable: "/leads/statuses/available",
    getListNames: "/leads/list-names",
    getTags: "/leads/tags",
    
    // Lead Beneficiaries endpoints
getLeadBeneficiaries: (leadId: string) => `/leads/beneficiaries/${leadId}`,
createLeadBeneficiaries: "/leads/beneficiaries",
updateLeadBeneficiary: (leadId: string, beneficiaryId: string) => `/leads/beneficiaries/${beneficiaryId}`,
deleteLeadBeneficiary: (leadId: string, beneficiaryId: string) => `/leads/beneficiaries/${beneficiaryId}`,  
    // Household endpoints (migrated from beneficiaries)
    getLeadHouseholds: (leadId: string) => `/leads/${leadId}/households`,
    restoreHouseholdMember: (leadId: string) => `/leads/${leadId}/household-restore`,
    createHousehold: "/households",
    addHouseholdMember: (householdId: string) => `/households/${householdId}/members`,
    removeHouseholdMember: (leadId: string) => `/households/members/${leadId}`,
    switchPrimaryMember: (householdId: string) => `/households/${householdId}/primary`,
    disbandHousehold: (householdId: string) => `/households/${householdId}/disband`,
    linkHouseholdLead: "/households/link",
    getHousehold: (householdId: string) => `/households/${householdId}`,
  },

  reminders: {
    getReminders: "/reminders",
    createReminder: "/reminders",
    getReminderById: (reminderId: string) => `/reminders/${reminderId}`,
    updateReminder: (reminderId: string) => `/reminders/${reminderId}`,
    updateReminderStatus: (reminderId: string) => `/reminders/${reminderId}/status`,
    deleteReminder: (reminderId: string) => `/reminders/${reminderId}`,
    getRemindersStats: "/reminders/stats",
  },

  subscriptionPlans: {
    getSubscriptionPlans: "/subscription-plans",
    getSubscriptionPlanById: (id: string) => `/subscription-plans/${id}`,
  },

  plans: {
    checkTrialEligibility: "/plans/check-trial-eligibility",
    checkTrialEligibilityPublic: (trialType: string, npn: string, email: string) => `/plans/check-trial-eligibility-public?trialType=${trialType}&npn=${npn}&email=${email}`,
    getAvailablePlans: "/plans/available",
  },

  // Trial status endpoints
  trialStatus: {
    getStatus: "/auth/status",
    getSubscriptionStatus: "/subscriptions/my-active-subscription",
    checkEligibility: "/subscription-plans/trial-eligibility",
    startTrial: "/subscriptions/start-trial",
    extendTrial: "/admin/trial/extend",
    getUsage: "/trial/status",
  },

  // Subscription sync endpoints
  subscriptionSync: {
    syncMySubscription: "/subscriptions/sync/my-subscription",
  },

  leadAssignment: {
    getUnassignedLeads: "/lead-assignment/unassigned",
    getCustomFieldValues: "/lead-assignment/custom-field-values", // GET ?key=&<the /unassigned params> - values among the table's leads
    getAgentWorkload: "/lead-assignment/agents/workload",
    getStatusTracking: "/lead-assignment/status-tracking",
    getAgentStatusBreakdown: "/lead-assignment/agent-status-breakdown",
    getActivityFeed: "/lead-assignment/activity-feed",
    getMilestones: "/lead-assignment/milestones",
    assignLead: "/lead-assignment/assign",
    unassignLead: "/lead-assignment/unassign",
    reassignLead: "/lead-assignment/reassign",
    assignFromImport: "/lead-assignment/assign-from-import",
  },

  voicemail: {
    // Voicemail endpoints are under /communication/voicemails
    getVoicemails: "/communication/voicemails",
    getVoicemailById: (id: string) => `/communication/voicemails/${id}`,
    createVoicemail: "/communication/voicemails",
    updateVoicemail: (id: string) => `/communication/voicemails/${id}`,
    deleteVoicemail: (id: string) => `/communication/voicemails/${id}`,
    markAsListened: (id: string) => `/communication/voicemails/${id}/mark-read`,
  },

  transactions: {
    getTransactions: "/transactions",
    getPurchaseHistory: "/transaction/purchase-history",
    getPurchaseHistoryStats: "/transaction/purchase-history/stats",
    getTransactionById: (id: string) => `/transaction/${id}/info`,
    getTransactionStats: "/transactions/stats",
    processRefund: "/transactions/refund",
    cancelTransaction: "/transaction/cancel",
    getTransactionRefunds: (id: string) => `/transactions/${id}/refunds`,
    downloadInvoice: (id: string) => `/transactions/${id}/invoice`,
    downloadReceipt: (id: string) => `/transactions/${id}/receipt`,
  },
  communication: {
    getCommunication: "/communication/area-codes", // Legacy endpoint - will be deprecated
    getPhoneNumbers: "/communication/phone-numbers", // NEW: DB-backed states & area codes (requires auth)
    getPhoneNumbersPublic: "/communication/phone-numbers/public", // Public endpoint for signup (no auth)
    getAreaCodesPublic: "/communication/area-codes/public", // Public endpoint for area codes in signup (no auth)
    getPhoneNumbersPricing: "/communication/phone-number-pricing/pricing", // UPDATED to match backend route (requires auth)
    getPhoneNumbersPricingPublic: "/communication/phone-number-pricing/public", // Public endpoint for signup (no auth)
    checkAreaNumberAvailable: "/communication/check-area-number-available",
    getAreaCodeAvailability: "/communication/phone-numbers/area-code-availability",
    getAreaCodeAvailabilitySingle: (code: string) => `/communication/phone-numbers/area-code-availability/${code}`,
    getAreaCodeCost: "/communication/area-codes/cost",
    startAreaNumberCheckout: "/communication/area-numbers/checkout", // UPDATED to match backend route
    subscribeToAreaCodes: "/communication/area-codes/subscribe",
    unreadCounts: "/communication/counts/unread",
    getSms: "/communication/sms",
    sendSms: "/communication/sms/send",
    sendBulkSms: "/communication/sms/send-bulk",
    sendEmail: "/communication/email",
    sendEmailViaOAuth: "/communication/email/oauth", // Send email via connected OAuth (Gmail/Outlook)
    downloadEmailAttachment: (messageId: string, attachmentId: string) => `/communication/email/oauth/attachment/${messageId}/${attachmentId}`, // Download attachment from Gmail/Outlook
    getEmailAttachments: (messageId: string) => `/communication/email/oauth/attachments/${messageId}`, // Get fresh attachment info with IDs from Gmail/Outlook
    getEmails: "/communication/emails", // Get email history for user
    getEmailsForLead: (leadId: string) => `/communication/emails/lead/${leadId}`, // Get emails for specific lead
    getEmailStats: "/communication/emails/stats", // Get email statistics (open rate, click rate, etc.)
    markEmailOpened: (emailId: string) => `/communication/emails/${emailId}/opened`, // Mark email as opened (manual tracking)
    contactCard: "/communication/contact-card",
    sendVoicemail: "/communication/voicemail/send",
    getCalls: "/communication/calls",
    getStats: "/communication/stats", // Get communication statistics (calls, SMS, emails)
    getDialerBreakdown: "/communication/dialer-breakdown", // Outbound calls by dialer: manual/power/multi-line
    getCallById: (callId: string) => `/communication/calls/${callId}`,
    getCallStatus: (callSid: string) => `/communication/calls/${callSid}/status`,
    // Call control endpoints
    toggleCallRecording: (callSid: string) => `/communication/calls/${callSid}/recording/toggle`,
    startCallRecording: (callSid: string) => `/communication/calls/${callSid}/recording`,
    holdCall: (callSid: string) => `/communication/calls/${callSid}/hold`,
    cancelCall: (callSid: string) => `/communication/calls/${callSid}/cancel`,
    initiateCall: "/communication/calls/initiate",
    initiateMultiLineCall: "/communication/calls/multi-line/initiate",
    initiateRaceToAnswer: "/communication/race-to-answer/initiate",
    getMultiLineSession: (sessionId: string) => `/communication/calls/multi-line/session/${sessionId}`,
    manualCall: "/communication/sessions/manual-call",
    // Call-wide processing endpoints (automatic integration)
    dtmfProcessCallWide: "/communication/dtmf/process-call-wide",
    dtmfProcessCallWideTyped: "/communication/dtmf/process-call-wide-typed",
    // A2P Verification endpoints
    a2p: {
      status: "/communication/a2p/status",
      verify: "/communication/a2p/verify",
      history: "/communication/a2p/history",
      checkEIN: "/communication/a2p/check-ein",
      verifyLowVolume: "/communication/a2p/verify/low-volume",
      retry: "/communication/a2p/retry",
      pendingNumbers: "/communication/a2p/pending-numbers",
      paymentCheck: "/communication/a2p/payment/check",
      paymentCreateSession: "/communication/a2p/payment/create-session",
      paymentStatus: (paymentId: string) => `/communication/a2p/payment/status/${paymentId}`,
      paymentByVerification: (verificationId: string) => `/communication/a2p/payment/by-verification/${verificationId}`,
      relinkPhoneNumbers: "/communication/a2p/relink-phone-numbers",
      uploadCp575: "/communication/a2p/upload-cp575",
      otpVerify: "/agency/a2p/otp/verify",
      otpResend: "/agency/a2p/otp/resend",
      callerId: "/agency/a2p/caller-id",
      registrationFee: "/agency/a2p/fee",
      registrationFeePay: "/agency/a2p/fee/pay",
    },
    // Voice/Calling endpoints
    getVoiceToken: "/communication/voice/token",
    // New endpoints for communication services
    sendVerification: "/auth/send-verification",
    setupIntents: "/stripe/setup-intents",
    savePaymentMethod: "/stripe/payment-methods/save",
    smartAreaCodeInsights: "/communication/smart-area-code-insights",
    areaCodePurchaseRecommendations: "/communication/area-code-purchase-recommendations",
    analyzeLeadsAreaCodes: "/communication/area-codes/analyze-leads", // Pre-dial check: analyzes selected leads for missing area codes
    // Number Health endpoints
    numberHealth: {
      dashboard: "/communication/number-health/dashboard",
      list: "/communication/number-health/numbers",
      detail: (id: string) => `/communication/number-health/numbers/${id}`,
      refresh: (id: string) => `/communication/number-health/numbers/${id}/refresh`,
      refreshAll: "/communication/number-health/refresh-all",
    },
  },

  // OAuth Email & Calendar Integration
  oauth: {
    base: "/api/oauth", // Base OAuth endpoint for dynamic routes
    // Email OAuth endpoints
    connectGoogle: "/api/oauth/google/connect", // Initiate Google OAuth (email + calendar scopes)
    connectMicrosoft: "/api/oauth/microsoft/connect", // Initiate Microsoft OAuth (email + calendar scopes)
    handleCallback: "/api/oauth/callback", // OAuth callback handler
    getConnectionStatus: "/api/oauth/connections", // Get all OAuth connections status
    disconnectProvider: (provider: 'google' | 'microsoft') => `/api/oauth/${provider}/disconnect`,
    checkTokenHealth: (provider: 'google' | 'microsoft') => `/api/oauth/${provider}/health`,
    getCalendarList: (provider: 'google' | 'microsoft') => `/api/oauth/${provider}/calendars`,
    selectCalendar: (provider: 'google' | 'microsoft') => `/api/oauth/${provider}/calendars/select`,

    // Email reading endpoints
    getEmails: "/api/oauth/emails", // List emails from connected provider
    getEmail: (messageId: string) => `/api/oauth/emails/${messageId}`, // Get single email details

    // Lead-specific email endpoints
    getLeadEmails: (leadId: string) => `/api/oauth/leads/${leadId}/emails`, // Get email history for lead (from DB)
    syncLeadEmails: (leadId: string) => `/api/oauth/leads/${leadId}/emails/sync`, // Sync lead emails from provider
    getLeadEmailsLive: (leadId: string) => `/api/oauth/leads/${leadId}/emails/live`, // Get live emails from provider

    // Calendar sync endpoints
    getCalendarPreferences: "/api/oauth/calendar/preferences",
    updateCalendarPreferences: "/api/oauth/calendar/preferences",
    getCalendarConnections: "/api/oauth/calendar/sync/status", // Backend route is calendar/sync/status

    // Manual sync
    syncAppointment: (appointmentId: string) => `/api/oauth/calendar/appointments/${appointmentId}/sync`,
    syncAllAppointments: "/api/oauth/calendar/sync/manual",

    // Webhook management (for backend use)
    renewWebhook: (provider: 'google' | 'microsoft') => `/api/oauth/calendar/webhook/${provider}/renew`,

    // Sync conflicts
    getSyncConflicts: "/api/oauth/calendar/conflicts",
    resolveSyncConflict: (conflictId: string) => `/api/oauth/calendar/conflicts/${conflictId}/resolve`,
  },

  areaCodes: {
    checkout: "/area-codes/checkout",
    availabilityLive: "/area-codes/availability/live",
    autoPurchase: "/area-codes/auto-purchase",
    validatePromoCode: "/area-codes/validate-promo-code",
  },

  areaCodePreferences: {
    get: "/area-code-preferences",
    updateMatchingStrategy: "/area-code-preferences/matching-strategy",
  },

  subscriptions: {
    checkoutSession: "/subscriptions/checkout-sessions",
    upgrade: "/subscriptions/upgrade",
    completeDryRunSubscription: "/subscriptions/dry-run/complete-subscription",
    getMyActiveSubscription: "/subscriptions/my-active-subscription",
    getMySubscriptions: "/subscriptions/my-subscriptions",
    cancelSubscription: (id: string) => `/subscriptions/${id}/cancel`,
    reactivateSubscription: (id: string) => `/subscriptions/${id}/reactivate`,
    updateSubscription: (id: string) => `/subscriptions/${id}`,
    getSubscriptionStats: "/subscriptions/stats",
    getUsageSummary: "/subscriptions/usage-summary",
    getUsageStats: "/subscriptions/usage-stats",
    getRecurringCharges: "/subscriptions/recurring-charges",
  },
  upload: {
    getSignedUrl: "/upload/signed-url",
    verifyUpload: "/upload/verify",
    getDownloadUrl: (uploadId: string) => `/upload/${uploadId}/download-url`,
    getUploadStatus: (uploadId: string) => `/upload/${uploadId}/status`,
    verifyHybrid: "/upload/verify-hybrid",
  },

  resources: {
    getResources: "/resources",
    getResourceById: (id: string) => `/resources/${id}`,
    createResource: "/resources",
    updateResource: (id: string) => `/resources/${id}`,
    deleteResource: (id: string) => `/resources/${id}`,
    likeResource: (id: string) => `/resources/${id}/like`,
    unlikeResource: (id: string) => `/resources/${id}/like`, // DELETE request
    getFilteredResources: "/resources",
    getCategories: "/resources/categories",
    getCarriers: "/agency-carriers", // Use same endpoint as policy creation
    getResourcesStats: "/resources/stats",
    uploadResource: "/resources/upload",
    viewResource: (id: string) => `/resources/${id}/view`, // View resource with proper Content-Type headers
    downloadResource: (id: string) => `/resources/${id}/download`,
    getDownloadUrl: (id: string) => `/resources/${id}/download-url`,
    trackDownload: (id: string) => `/resources/${id}/download`,
    bulkTrackDownloads: "/resources/bulk-download",
    
    // File notification endpoints
    notifyFileUpload: "/resources/files/notify-upload",
    notifyFileDelete: "/resources/files/notify-delete",
    notifyFileMove: "/resources/files/notify-move",
    updateFileMetadata: "/resources/files/update-metadata",
    bulkFileOperation: "/resources/files/bulk-operation",
    syncFileStatus: "/resources/files/sync-status",
    getFileProcessingStatus: (bucket: string, path: string) => 
      `/resources/files/processing-status?bucket=${encodeURIComponent(bucket)}&path=${encodeURIComponent(path)}`,
    
    // File access and permissions endpoints
    checkFilePermission: "/resources/files/check-permission",
    grantFileAccess: "/resources/files/grant-access",
    revokeFileAccess: "/resources/files/revoke-access",
    getFilePermissions: "/resources/files/permissions",
    getUserFileAccess: (userId: string) => `/resources/files/user-access/${userId}`,
    logFileAccess: "/resources/files/log-access",
    getFileAccessLogs: "/resources/files/access-logs",
    validateFileAccess: "/resources/files/validate-access",
    checkFilePublicStatus: "/resources/files/public-status",
    getFileOwner: "/resources/files/owner",
  },
  sales: {
    getPremiumAnnual: "/transaction/premium-sales/annual",
    getPremiumAnnualAgency: (agencyId: string) => `/transaction/premium-sales/annual/agency/${agencyId}`,
    getPremiumMonthly: "/transaction/premium-sales/monthly",
    getPremiumMonthlyAgency: (agencyId: string) => `/transaction/premium-sales/monthly/agency/${agencyId}`,
    getBestPremiumMonthly: "/transaction/premium-sales/best-months",
    getBestPremiumMonthlyAgency: (agencyId: string) => `/transaction/premium-sales/best-months/agency/${agencyId}`,
    getBestPremiumWeekly: "/transaction/premium-sales/best-weeks",
    getBestPremiumWeeklyAgency: (agencyId: string) => `/transaction/premium-sales/best-weeks/agency/${agencyId}`,
  },
  dashboard: {
    getOverview: "/dashboard/overview",
    getOverviewWithFilter: (agencyId?: string, period?: string, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (agencyId) params.append('agencyId', agencyId);
      if (period) params.append('period', period);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return queryString ? `/dashboard/overview?${queryString}` : "/dashboard/overview";
    },
  },
  callScripts: {
    list: "/call-scripts",
    detail: (id: string) => `/call-scripts/${id}`,
    rewrite: (id: string) => `/call-scripts/${id}/rewrite`,
    generate: "/call-scripts/generate",
  },

  smsSettings: {
    getSmsTemplates: "/sms-templates",
    deleteSmsTemplate: (id: string) => `/sms-templates/${id}`,
    getSmsSettings: "/users/profile/sms-settings/with-template",
    updateSmsSettings: "/users/profile/sms-settings",
  },

  liveTransfer: {
    campaigns: "/live-transfer/campaigns",
    campaign: (id: string) => `/live-transfer/campaigns/${id}`,
    campaignActivity: (id: string) => `/live-transfer/campaigns/${id}/activity`,
    overview: "/live-transfer/overview",
    availableNumbers: "/live-transfer/available-numbers",
    tollFreeNumber: "/live-transfer/toll-free-number",
    eligibleAgents: "/live-transfer/eligible-agents",
    apiAccess: "/live-transfer/api-access",
    appointmentApi: "/live-transfer/appointment-api",
    appointmentApiStatus: "/live-transfer/appointment-api/status",
    appointmentApiAppointments: "/live-transfer/appointment-api/appointments",
    appointmentApiRegenerate: "/live-transfer/appointment-api/regenerate",
  },
  agency: {
    getAgencies: "/agencies",
    getAgenciesHierarchy: "/agencies/hierarchy", // ✅ Backend now implements this endpoint
    getAgencyById: (id: string) => `/agencies/${id}`,
    createAgency: "/agencies/sub-agencies",
    createSubAgency: "/agencies/sub-agencies",
    getSubAgencies: "/agencies/sub-agencies/fetch",
    getSubAgencyTree: (agencyId: string) => `/agencies/${agencyId}/sub-agency-tree`,
    updateAgency: (id: string) => `/agencies/${id}`,
    deleteAgency: (id: string) => `/agencies/${id}`,
    agencyStats: (id: string) => `/agencies/${id}/stats/summary`,
    agencyStatsAgents: (id: string) => `/agencies/${id}/stats/agents`,
    getAgentStats: (id: string) => `/agencies/${id}/stats/agents`, // Alias for agencyStatsAgents
    agencyUsers: (id: string) => `/agencies/${id}/users/flexible`,
    getAgencyUsers: (id: string) => `/agencies/${id}/users`,
    getAgentsWorkload: (id: string) => `/agencies/${id}/agents/workload`,
    updateAgencyUserRole: (agencyId: string, userId?: string) => userId ? `/agencies/${agencyId}/users/${userId}/role` : `/agencies/${agencyId}/users/role`,
    deleteAgencyUser: (id: string) => `/agencies/${id}/users`,
    disableEligibility: (id: string, userId: string) => `/agencies/${id}/users/${userId}/disable-eligibility`,
    suspendAgent: (id: string, userId: string) => `/agencies/${id}/users/${userId}/suspend`,
    unsuspendAgent: (id: string, userId: string) => `/agencies/${id}/users/${userId}/unsuspend`,
    restoreAgent: (id: string, userId: string) => `/agencies/${id}/users/${userId}/restore`,
    requestDeactivation: (id: string, userId: string) => `/agencies/${id}/users/${userId}/deactivation-request`,
    moveUser: (id: string) => `/agencies/${id}/move-user`,
    moveUsersBulk: (id: string) => `/agencies/${id}/move-users-bulk`,
    getPerformanceTrends: (id: string) => `/agencies/${id}/stats/performance-trends`,
    getSubAgencyStats: (id: string) => `/agencies/${id}/stats/sub-agencies`,
    getSubAgencyTreeStats: (id: string) => `/agencies/${id}/stats/sub-agency-tree`,
    getSubAgentStats: (id: string) => `/agencies/${id}/sub-agencies/agent-stats`,
    getIndividualUserStats: (agencyId: string, userId: string) => `/agencies/${agencyId}/stats/user/${userId}`,
    getPremiumByCarrier: (agencyId: string) => `/agencies/${agencyId}/stats/premium-by-carrier`,
    getDialEfficiency: (agencyId: string) => `/agencies/${agencyId}/stats/dial-efficiency`,
    getPolicyStatsByState: (agencyId: string) => `/agencies/${agencyId}/stats/policy-map`,
    getLeadStatsByState: (agencyId: string) => `/agencies/${agencyId}/stats/lead-map`,
    getBillingSettings: (agencyId: string) => `/agencies/${agencyId}/billing-settings`,
    updateBillingSettings: (agencyId: string) => `/agencies/${agencyId}/billing-settings`,
    getLeaderboardSettings: (agencyId: string) => `/agencies/${agencyId}/leaderboard-settings`,
    updateLeaderboardSettings: (agencyId: string) => `/agencies/${agencyId}/leaderboard-settings`,
    getExportSettings: (agencyId: string) => `/agencies/${agencyId}/export-settings`,
    updateExportSettings: (agencyId: string) => `/agencies/${agencyId}/export-settings`,
    getAgencyWideSettings: (agencyId: string) => `/agencies/${agencyId}/agency-wide-settings`,
    updateAgencyWideSettings: (agencyId: string) => `/agencies/${agencyId}/agency-wide-settings`,
    getAdminCallingSettings: (agencyId: string) => `/agencies/${agencyId}/admin-calling-settings`,
    masterExport: '/agencies/master-export',
    masterExportOptions: '/agencies/master-export/options',
    updateAdminCallingSettings: (agencyId: string) => `/agencies/${agencyId}/admin-calling-settings`,
    // New endpoints for agency management
    createForIndependent: "/agencies/create-for-independent",
    canCreate: "/agencies/can-create",
  },

  agencySubscription: {
    enterpriseStatus: "/agency-subscription/enterprise-status",
    hierarchyAgents: "/agency-subscription/hierarchy-agents",
    seatSummary: "/agency-subscription/seat-summary",
    toggleCoverage: "/agency-subscription/toggle-coverage",
    bulkToggleCoverage: "/agency-subscription/bulk-toggle-coverage",
  },

  agencyTv: {
    getSettings: (agencyId: string) => `/agencies/${agencyId}/tv-display`,
    updateSettings: (agencyId: string) => `/agencies/${agencyId}/tv-display`,
    lockSessions: (agencyId: string) => `/agencies/${agencyId}/tv-display/lock-sessions`,
  },

  adminAgencies: {
    enterpriseOverview: "/admin/agencies/enterprise-overview",
    setSeatLimit: (id: string) => `/admin/agencies/${id}/seat-limit`,
  },

  leaderboard: {
    getStats: "/leaderboard/stats",
    getLeaderboardStats: "/leaderboard/stats",
    getUserVisibilitySettings: "/leaderboard/visibility",
    updateUserVisibilitySettings: "/leaderboard/visibility",
    toggleVisibility: (visible: boolean) => `/leaderboard/visibility/toggle?visible=${visible ? 'true' : 'false'}`,
    toggleAnonymous: (hide: boolean) => `/leaderboard/visibility/toggle-anonymous?hide=${hide ? 'true' : 'false'}`,
    // Global leaderboard endpoints
    global: {
      get: (period: string) => `/leaderboard/global?period=${period}`,
      settings: "/leaderboard/global/settings",
    },
  },

  // AI Service endpoints
  ai: {
    // Core AI endpoints
    chat: "/ai/chat",
    underwriting: "/ai/underwriting",
    stateMapping: "/ai/state-mapping",

    // Chat history management
    chatHistory: "/ai/chat/history",
    getChatHistory: "/ai/chat/history",

    // System information
    providers: "/ai/providers",
    contexts: "/ai/contexts",
    contextStats: "/ai/contexts/stats",

    // Knowledge base
    knowledgeBaseStats: "/ai/knowledge-base/stats",
    searchKnowledgeBase: "/ai/knowledge-base/search",
    refreshKnowledgeBase: "/ai/knowledge-base/refresh",

    // Performance monitoring
    performance: "/ai/performance",
    health: "/ai/health",

    // AI Underwriting endpoints (N8N workflow)
    ingestDocument: "/uw/ingest",
    askQuestion: "/uw/answer",
    testConnection: "/uw/test",
  },

  chatbot: {
    // Unified AI System - Primary endpoint
    unifiedMessage: "/chatbot/unified/message",
    unifiedCardSession: "/chatbot/unified/card-session",
    unifiedMessageStream: "/chatbot/unified/message/stream", // SSE streaming endpoint
    unifiedHistory: (sessionId: string) => `/chatbot/unified/history/${sessionId}`,
    unifiedAnalytics: "/chatbot/unified/analytics",
    
    // Legacy endpoints (backward compatibility)
    message: "/chatbot/message",
    sendMessage: "/chatbot/message", // Backward compatibility
    createSession: "/chatbot/sessions",
    getSessions: "/chatbot/sessions",
    listSessions: "/chatbot/sessions", // Backward compatibility
    getHistory: (sessionId: string) => `/chatbot/history/${sessionId}`,
    getHistoryFlat: (sessionId: string) => `/chatbot/history/${sessionId}/flat`, // Backward compatibility
    endSession: (sessionId: string) => `/chatbot/sessions/${sessionId}/end`,
    switchContext: "/chatbot/sessions", // Universal context switcher endpoint
    analytics: "/chatbot/analytics",
    getAnalytics: "/chatbot/analytics", // Backward compatibility
    sampleQuestions: "/chatbot/sample-questions", // NEW: Sample questions endpoint
  },

  // AI Calling System
  aiCalling: {
    // Agent Config
    config: "/ai-calling/config",
    configById: (id: string) => `/ai-calling/config/${id}`,
    testConfig: (id: string) => `/ai-calling/config/${id}/test`,
    voices: "/ai-calling/config/voices",
    providers: "/ai-calling/config/providers",
    promptTemplates: "/ai-calling/config/prompt-templates",
    generateScript: "/ai-calling/config/generate-script",
    // Campaigns
    campaigns: "/ai-calling/campaigns",
    campaignById: (id: string) => `/ai-calling/campaigns/${id}`,
    populateLeads: (id: string) => `/ai-calling/campaigns/${id}/populate-leads`,
    previewLeads: "/ai-calling/campaigns/preview-leads",
    startCampaign: (id: string) => `/ai-calling/campaigns/${id}/start`,
    pauseCampaign: (id: string) => `/ai-calling/campaigns/${id}/pause`,
    resumeCampaign: (id: string) => `/ai-calling/campaigns/${id}/resume`,
    campaignLeads: (id: string) => `/ai-calling/campaigns/${id}/leads`,
    campaignStats: (id: string) => `/ai-calling/campaigns/${id}/stats`,
    instantCall: "/ai-calling/campaigns/instant-call",
    // Objection Handlers
    objections: "/ai-calling/objections",
    objectionById: (id: string) => `/ai-calling/objections/${id}`,
    reorderObjections: "/ai-calling/objections/reorder",
    // Monitoring
    dashboard: "/ai-calling/monitoring/dashboard",
    activeCalls: "/ai-calling/monitoring/active-calls",
    recentDispositions: "/ai-calling/monitoring/recent-dispositions",
    // Usage
    currentUsage: "/ai-calling/usage/current",
    usageHistory: "/ai-calling/usage/history",
    // AI Texting (two-way SMS assistant)
    textingEligibility: "/ai-calling/texting/eligibility",
    textingStats: "/ai-calling/texting/stats",
    textingConversations: "/ai-calling/texting/conversations",
    textingConversationById: (id: string) => `/ai-calling/texting/conversations/${id}`,
    textingStart: "/ai-calling/texting/start",
    textingTakeOver: (id: string) => `/ai-calling/texting/conversations/${id}/take-over`,
    textingClose: (id: string) => `/ai-calling/texting/conversations/${id}/close`,
  },

  // Dial Credits — wallet for AI Outbound Calling
  dialCredits: {
    balance: "/dial-credits/balance",
    transactions: "/dial-credits/transactions",
    checkout: "/dial-credits/checkout",
    settings: "/dial-credits/settings",
    config: "/dial-credits/config",
  },

  // Team Chat
  teamChat: {
    channels: "v1/team-chat/channels",
    channelMessages: (channelId: string) => `v1/team-chat/channels/${channelId}/messages`,
    channelMembers: (channelId: string) => `v1/team-chat/channels/${channelId}/members`,
    markRead: (channelId: string) => `v1/team-chat/channels/${channelId}/mark-read`,
    muteChannel: (channelId: string) => `v1/team-chat/channels/${channelId}/mute`,
    sendMessage: (channelId: string) => `v1/team-chat/channels/${channelId}/messages`,
    editMessage: (messageId: string) => `v1/team-chat/messages/${messageId}`,
    deleteMessage: (messageId: string) => `v1/team-chat/messages/${messageId}`,
    addReaction: (messageId: string) => `v1/team-chat/messages/${messageId}/reactions`,
    removeReaction: (messageId: string, emoji: string) => `v1/team-chat/messages/${messageId}/reactions/${emoji}`,
    dm: "v1/team-chat/dm",
    unreadCounts: "v1/team-chat/unread-counts",
    agencyStats: "v1/team-chat/agency-stats",
    uploadFile: (channelId: string) => `v1/team-chat/channels/${channelId}/upload`,
  },

  // Community Forum ("Reddit for life insurance agents") — one global community
  forum: {
    eligibility: "v1/forum/eligibility",
    board: "v1/forum/board",
    search: "v1/forum/search",
    categories: "v1/forum/categories",
    posts: "v1/forum/posts",
    latest: "v1/forum/posts/latest",
    post: (idOrSlug: string) => `v1/forum/posts/${idOrSlug}`,
    comments: (postId: string) => `v1/forum/posts/${postId}/comments`,
    comment: (commentId: string) => `v1/forum/comments/${commentId}`,
    votes: "v1/forum/votes",
    reports: "v1/forum/reports",
    media: "v1/forum/media",
    user: (userId: string) => `v1/forum/users/${userId}`,
    myState: "v1/forum/me/state",
    agencySettings: "v1/forum/agency-settings",
    mod: {
      reports: "v1/forum/mod/reports",
      screenings: "v1/forum/mod/screenings",
      report: (id: string) => `v1/forum/mod/reports/${id}`,
      postAction: (id: string, action: string) => `v1/forum/mod/posts/${id}/${action}`,
      commentAction: (id: string, action: string) => `v1/forum/mod/comments/${id}/${action}`,
      ban: (userId: string) => `v1/forum/mod/users/${userId}/ban`,
      unban: (userId: string) => `v1/forum/mod/users/${userId}/unban`,
    },
  },

  // AI Underwriting System
  aiUnderwriting: {
    // Document ingestion
    ingestDocument: "/ai-underwriting/documents/ingest",
    verifyDocumentFreshness: "/uw/verify-freshness",
    validateContentAccuracy: "/uw/validate-accuracy",
    
    // Answer pipeline
    askQuestion: "/ai-underwriting/ask",
    getAnswer: "/ai-underwriting/answer",
    
    // Document management
    getDocuments: "/ai-underwriting/documents",
    getDocumentById: (id: string) => `/ai-underwriting/documents/${id}`,
    deleteDocument: (id: string) => `/ai-underwriting/documents/${id}`,
    
    // Analytics and monitoring
    getAnalytics: "/ai-underwriting/analytics",
    getAuditLog: "/ai-underwriting/audit-log",
    getPerformanceMetrics: "/ai-underwriting/performance",
    
    // Carrier management
    getCarriers: "/ai-underwriting/carriers",
    getCarrierDocuments: (carrierSlug: string) => `/ai-underwriting/carriers/${carrierSlug}/documents`,
    requestCarrier: "/ai-underwriting/carriers/request",
  },

  fax: {
    sendFax: "/fax/send",
    getFaxHistory: "/fax/history",
    getDailyUsage: "/fax/daily-usage",
    getDocument: (faxId: string, documentIndex: number) => `/fax/${faxId}/document/${documentIndex}`,
  },

policy: {
  createPolicy: "/policies",
  updatePolicy: (policyId: string) => `/policies/${policyId}`, // ✅ Fixed path
  deletePolicy: (policyId: string) => `/policies/${policyId}`,
},

ers: {
  createSession: "/ers/sessions",
  updateSession: (sessionId: string) => `/ers/sessions/${sessionId}`,
  deleteSession: (sessionId: string) => `/ers/sessions/${sessionId}`,
  sendMessages: (sessionId: string) => `/ers/sessions/${sessionId}/send`,
  getSessionsByLead: (leadId: string) => `/ers/sessions/lead/${leadId}`,
  getSession: (sessionId: string) => `/ers/sessions/${sessionId}`,
  getDashboard: "/ers/dashboard",
  markConverted: (contactId: string) => `/ers/contacts/${contactId}/convert`,
  updateContact: (contactId: string) => `/ers/contacts/${contactId}`,
  getTemplates: "/ers/templates",
},

policyGuard: {
  scan: "/policy-guard/scan",
  getAlerts: "/policy-guard/alerts",
  getDashboard: "/policy-guard/dashboard",
  getLeadAlerts: (leadId: string) => `/policy-guard/lead/${leadId}`,
  updateAlert: (alertId: string) => `/policy-guard/alerts/${alertId}`,
},
  stripe: {
    createPaymentIntent: "/stripe/payment-intents",
    confirmPaymentIntent: (id: string) => `/stripe/payment-intents/${id}/confirm`,
    createSetupIntent: "/stripe/setup-intents",
    confirmSetupIntent: (id: string) => `/stripe/setup-intents/${id}/confirm`,
    createCustomer: "/stripe/customers",
    getCustomer: (id: string) => `/stripe/customers/${id}`,
    updateCustomer: (id: string) => `/stripe/customers/${id}`,
    getCustomerPaymentMethods: (id: string) => `/stripe/customers/${id}/payment-methods`,
    deletePaymentMethod: (id: string) => `/stripe/payment-methods/${id}`,
    createSubscription: "/stripe/subscriptions",
    getSubscription: (id: string) => `/stripe/subscriptions/${id}`,
    updateSubscription: (id: string) => `/stripe/subscriptions/${id}`,
    cancelSubscription: (id: string) => `/stripe/subscriptions/${id}/cancel`,
    reactivateSubscription: (id: string) => `/stripe/subscriptions/${id}/reactivate`,
    createCheckoutSession: "/stripe/checkout-sessions",
    getPrices: "/stripe/prices",
    getProducts: "/stripe/products",
    getInvoices: "/stripe/invoices",
    getInvoice: (id: string) => `/stripe/invoices/${id}`,
    handleWebhook: "/stripe/webhooks",
    getPaymentMethods: "/stripe/payment-methods",
    attachPaymentMethod: "/stripe/payment-methods/attach",
    detachPaymentMethod: "/stripe/payment-methods/detach",
    // New endpoints for area code purchases
    checkoutSessions: "/stripe/checkout-sessions",
    verifyPurchases: "/stripe/verify-purchases",
  },

  // Phone number pricing endpoints
  phoneNumberPricing: {
    getPricing: "/communication/phone-number-pricing/pricing", // UPDATED to match backend route
    assignPhoneNumber: "/communication/phone-numbers/assign",
    getUserPhoneNumbers: "/communication/phone-numbers",
    getAvailablePhoneNumbers: (queryParams?: string) => `/communication/phone-numbers/available${queryParams ? `?${queryParams}` : ''}`,
    // New management endpoints
    deletePhoneNumber: (phoneId: string) => `/communication/phone-numbers/${phoneId}`,
    restorePhoneNumber: (phoneId: string) => `/communication/phone-numbers/${phoneId}/restore`,
    reassignPhoneNumber: (phoneId: string) => `/communication/phone-numbers/${phoneId}/assign`,
    getPhoneNumberDetails: (phoneId: string) => `/communication/phone-numbers/${phoneId}/details`,
  },

  // Compliance System Endpoints
  compliance: {
    // Dashboard and Overview
    dashboard: "/compliance/dashboard",
    stats: "/compliance/stats",
    
    // Framework Management
    frameworks: "/compliance/frameworks",
    getFramework: (id: string) => `/compliance/frameworks/${id}`,
    getFrameworkDetails: (id: string) => `/compliance/frameworks/${id}/details`,
    
    // Control Management
    controls: "/compliance/controls",
    getControl: (id: string) => `/compliance/controls/${id}`,
    
    // Evidence Management
    evidence: "/compliance/evidence",
    getEvidence: (id: string) => `/compliance/evidence/${id}`,
    getControlEvidence: (controlId: string) => `/compliance/controls/${controlId}/evidence`,
    verifyEvidence: (evidenceId: string) => `/compliance/evidence/${evidenceId}/verify`,
    
    // Alerts Management
    alerts: "/compliance/alerts",
    markAlertAsRead: (alertId: string) => `/compliance/alerts/${alertId}/read`,
    markAllAlertsAsRead: "/compliance/alerts/mark-all-read",
    
    // History and Audit
    history: "/compliance/history",
    
    // Reports
    generateReport: "/compliance/reports/generate",
    
    // Assessment
    triggerAssessment: "/compliance/assess",
    
    // Search
    search: "/compliance/search",
  },
  agencies: {
    myStatus: "/agencies/my-status",
    // Agency agents endpoint
    agents: "/agencies/agents",
  },

  // Invite management endpoints
  invites: {
    cancelInvite: (inviteId: string) => `/invites/${inviteId}`,
    // Pending invites addressed to the logged-in user (dashboard "you were invited" prompt)
    mine: `/invites/mine`,
    acceptSelf: (inviteId: string) => `/invites/${inviteId}/accept-self`,
  },
  
  // Admin endpoints for duplicate accounts
  admin: {
    platformAnnouncements: {
      list: "/admin/platform-announcements",
      get: (id: string) => `/admin/platform-announcements/${id}`,
      create: "/admin/platform-announcements",
      update: (id: string) => `/admin/platform-announcements/${id}`,
      delete: (id: string) => `/admin/platform-announcements/${id}`,
    },
    duplicateAccounts: {
      flags: "/admin/duplicate-accounts/flags",
      getFlags: (flagId: string) => `/admin/duplicate-accounts/flags/${flagId}`,
      reviewFlag: (flagId: string) => `/admin/duplicate-accounts/flags/${flagId}/review`,
      resolveFlag: (flagId: string) => `/admin/duplicate-accounts/flags/${flagId}/resolve`,
      dismissFlag: (flagId: string) => `/admin/duplicate-accounts/flags/${flagId}/dismiss`,
      getByNPN: (npn: string) => `/admin/duplicate-accounts/by-npn/${npn}`,
      getByEmail: (email: string) => `/admin/duplicate-accounts/by-email/${email}`,
      getByPhone: (phone: string) => `/admin/duplicate-accounts/by-phone/${phone}`,
      stats: "/admin/duplicate-accounts/stats"
    },
    trialUsers: {
      list: "/admin/trial-users",
      get: (userId: string) => `/admin/trial-users/${userId}`,
      extend: (userId: string) => `/admin/trial-users/${userId}/extend`,
      convert: (userId: string) => `/admin/trial-users/${userId}/convert`,
      suspend: (userId: string) => `/admin/trial-users/${userId}/suspend`,
      reactivate: (userId: string) => `/admin/trial-users/${userId}/reactivate`,
      usage: (userId: string) => `/admin/trial-users/${userId}/usage`,
      analytics: "/admin/trial-users/analytics",
      sendReminder: (userId: string) => `/admin/trial-users/${userId}/send-reminder`,
      stats: "/admin/trial-users/stats",
      // Bulk extend
      bulkExtend: "/admin/trial-users/bulk-extend",
    },
    users: {
      all: "/admin/users/all",
      suspended: "/admin/users/suspended",
      deactivationRequests: "/admin/users/deactivation-requests",
      approveDeactivation: (id: string) => `/admin/users/deactivation-requests/${id}/approve`,
      rejectDeactivation: (id: string) => `/admin/users/deactivation-requests/${id}/reject`,
      deleted: "/admin/users/deleted",
      reactivate: (userId: string) => `/admin/users/${userId}/reactivate`,
      extendSuspension: (userId: string) => `/admin/users/${userId}/extend-suspension`,
      permanentSuspend: (userId: string) => `/admin/users/${userId}/permanent-suspend`,
      recover: (userId: string) => `/admin/users/${userId}/recover`,
      permanentDelete: (userId: string) => `/admin/users/${userId}/permanent-delete`,
    },
    userApprovals: {
      list: "/admin/user-approvals",
      get: (userId: string) => `/admin/user-approvals/${userId}`,
      approve: (userId: string) => `/admin/user-approvals/${userId}/approve`,
      reject: (userId: string) => `/admin/user-approvals/${userId}/reject`,
      getDuplicates: (userId: string) => `/admin/user-approvals/${userId}/duplicates`,
      bulkApprove: "/admin/user-approvals/bulk-approve",
      bulkReject: "/admin/user-approvals/bulk-reject",
      stats: "/admin/user-approvals/stats"
    }
  },

  // Utility endpoints
  currencyUtils: {
    format: "/api/utils/currency/format",
    parse: "/api/utils/currency/parse", 
    validate: "/api/utils/currency/validate",
    formatAnnualPremium: "/api/utils/currency/format-annual-premium",
    validateAnnualPremium: "/api/utils/currency/validate-annual-premium",
    formatForFrontend: "/api/utils/currency/format-for-frontend",
    formatDisplay: "/api/utils/currency/format-display",
    validateBatch: "/api/utils/currency/validate-batch"
  },

  phoneUtils: {
    sanitize: "/api/utils/phone/sanitize",
    validate: "/api/utils/phone/validate",
    format: "/api/utils/phone/format",
    areaCode: "/api/utils/phone/area-code",
    compare: "/api/utils/phone/compare",
    validateUS: "/api/utils/phone/validate-us",
    validateBatch: "/api/utils/phone/validate-batch"
  },

  // Lead Vendor Integration Portal Endpoints
  vendor: {
    // Admin endpoints (god mode panel)
    admin: {
      list: "/api/admin/vendors",
      create: "/api/admin/vendors",
      get: (id: string) => `/api/admin/vendors/${id}`,
      update: (id: string) => `/api/admin/vendors/${id}`,
      delete: (id: string) => `/api/admin/vendors/${id}`,
      regenerateKey: (id: string) => `/api/admin/vendors/${id}/regenerate-key`,
      getSubmissions: (id: string) => `/api/admin/vendors/${id}/submissions`,
      getLogs: (id: string) => `/api/admin/vendors/${id}/logs`,
      securityAlerts: "/api/admin/vendors/security/alerts",
      sendPromotion: (id: string) => `/api/admin/vendors/${id}/send-promotion`,
      getPromotionStatus: (id: string) => `/api/admin/vendors/${id}/promotion-status`,
    },
    // Vendor portal auth endpoints (public)
    auth: {
      login: "/api/v1/vendor/auth/login",
      mfaSetup: "/api/v1/vendor/auth/mfa/setup",
      mfaVerify: "/api/v1/vendor/auth/mfa/verify",
      mfaLogin: "/api/v1/vendor/auth/mfa/login",
    },
    // Vendor dashboard endpoints (requires session)
    dashboard: "/api/v1/vendor/dashboard",
    profile: "/api/v1/vendor/profile",
    submissions: "/api/v1/vendor/submissions",
    resyncSubmission: (id: string) => `/api/v1/vendor/submissions/${id}/resync`,
    resyncAll: "/api/v1/vendor/submissions/resync-all",
    stats: "/api/v1/vendor/stats",
    logs: "/api/v1/vendor/logs",
    topAgents: "/api/v1/vendor/top-agents",
    batches: "/api/v1/vendor/batches",
    resyncBatch: (id: string) => `/api/v1/vendor/batches/${id}/resync`,
    // Promotions/Notifications
    promotionStatus: "/api/v1/vendor/promotions/status",
    sendPromotion: "/api/v1/vendor/promotions/send",
    // Webhook endpoints (API key only)
    submitLeads: "/api/v1/vendor/leads",
    testConnection: "/api/v1/vendor/test",
    docs: "/api/v1/vendor/docs",
  },

  // Sentimentals System Endpoints
  sentimentals: {
    getSettings: "/sentimentals/settings",
    updateSettings: "/sentimentals/settings",
    getSchedules: "/sentimentals/schedules",
    approveSchedule: (id: string) => `/sentimentals/schedules/${id}/approve`,
    rejectSchedule: (id: string) => `/sentimentals/schedules/${id}/reject`,
    updateScheduleMessage: (id: string) => `/sentimentals/schedules/${id}/message`,
    approveAll: "/sentimentals/schedules/approve-all",
    getLogs: "/sentimentals/logs",
    testSend: "/sentimentals/test-send",
    getUpcomingOccasions: "/sentimentals/upcoming-occasions",
    getOptOuts: "/sentimentals/opt-outs",
    reEnableOptOut: (leadId: string) => `/sentimentals/opt-outs/${leadId}/re-enable`,
  },

  // On-demand call transcription (local Whisper)
  callTranscripts: {
    request: (callId: string) => `/call-transcripts/${callId}`,
    get: (callId: string) => `/call-transcripts/${callId}`,
    saveClient: (callId: string) => `/call-transcripts/${callId}/client`,
  },

  // DNC Scrub (wallet-billed via dial credits)
  leadScrub: {
    quote: "/lead-scrub/quote",
    start: "/lead-scrub/start",
    jobs: "/lead-scrub/jobs",
    job: (jobId: string) => `/lead-scrub/jobs/${jobId}`,
  },

  // Voice Clone Endpoints (ElevenLabs voice cloning + RVM)
  voiceClone: {
    list: "/voice-clone",
    upload: "/voice-clone/upload",
    delete: (id: string) => `/voice-clone/${id}`,
    setDefault: (id: string) => `/voice-clone/${id}/default`,
    preview: (id: string) => `/voice-clone/${id}/preview`,
    testRvm: (id: string) => `/voice-clone/${id}/test-rvm`,
  },

  // Agent API Access Endpoints (for agent funnels integration)
  agentApi: {
    // Agent-facing endpoints
    status: "/api/v1/agent-api/status",
    request: "/api/v1/agent-api/request",
    stats: "/api/v1/agent-api/stats",
    submissions: "/api/v1/agent-api/submissions",
    regenerateKey: "/api/v1/agent-api/regenerate-key",
    revoke: "/api/v1/agent-api/revoke",
    docs: "/api/v1/agent-api/docs",
    // Webhook endpoint for lead submission
    submitLeads: "/api/v1/agent-api/leads",
    // Admin endpoints
    admin: {
      pendingCount: "/admin/agent-api/pending-count",
      requests: "/admin/agent-api/requests",
      approve: (id: string) => `/admin/agent-api/requests/${id}/approve`,
      deny: (id: string) => `/admin/agent-api/requests/${id}/deny`,
      suspend: (id: string) => `/admin/agent-api/requests/${id}/suspend`,
      reactivate: (id: string) => `/admin/agent-api/requests/${id}/reactivate`,
      revoke: (id: string) => `/admin/agent-api/requests/${id}/revoke`,
      regenerateKey: (id: string) => `/admin/agent-api/requests/${id}/regenerate-key`,
      getSubmissions: (id: string) => `/admin/agent-api/requests/${id}/submissions`,
      // Security endpoints
      securityAlerts: "/admin/agent-api/security/alerts",
      securityEvents: "/admin/agent-api/security/events",
      blockedIps: "/admin/agent-api/security/blocked-ips",
      blockIp: "/admin/agent-api/security/block-ip",
      unblockIp: (ip: string) => `/admin/agent-api/security/block-ip/${encodeURIComponent(ip)}`,
      logsByIp: (ip: string) => `/admin/agent-api/security/logs-by-ip/${encodeURIComponent(ip)}`,
      // Per-access security endpoints
      getAccessSecurity: (id: string) => `/admin/agent-api/requests/${id}/security`,
      getAccessLogs: (id: string) => `/admin/agent-api/requests/${id}/logs`,
      getAccessAnalytics: (id: string) => `/admin/agent-api/requests/${id}/analytics`,
      // Settings update endpoint
      updateSettings: (id: string) => `/admin/agent-api/requests/${id}/settings`,
    },
  },

  routes: {
    optimize: "/routes/optimize",
    geocodeLeads: "/routes/geocode-leads",
    list: "/routes",
    getById: (routeId: string) => `/routes/${routeId}`,
    update: (routeId: string) => `/routes/${routeId}`,
    delete: (routeId: string) => `/routes/${routeId}`,
    followUp: (routeId: string) => `/routes/${routeId}/follow-up`,
    reOptimize: (routeId: string) => `/routes/${routeId}/re-optimize`,
    autoFixPin: (leadId: string) => `/routes/leads/${leadId}/auto-fix-pin`,
    saveManualPin: (leadId: string) => `/routes/leads/${leadId}/manual-pin`,
    correctAddress: (leadId: string) => `/routes/leads/${leadId}/correct-address`,
    checkIn: (routeId: string) => `/routes/${routeId}/check-in`,
    nearbyLeads: "/routes/nearby-leads",
    mileage: "/routes/mileage",
    bestTimes: "/routes/best-times",
    analytics: "/routes/analytics",
    territories: "/routes/territories",
    updateTerritory: (id: string) => `/routes/territories/${id}`,
    deleteTerritory: (id: string) => `/routes/territories/${id}`,
    notHome: (routeId: string) => `/routes/${routeId}/not-home`,
    fieldTag: (routeId: string) => `/routes/${routeId}/field-tag`,
    heatmap: "/routes/heatmap",
    knockStats: "/routes/knock-stats",
    knockHistory: (leadId: string) => `/routes/knock-history/${leadId}`,
    checkDuplicates: "/routes/check-duplicates",
  },

  features: {
    myAgency: "/features/my-agency",
    forAgency: (agencyId: string) => `/features/${agencyId}`,
    check: (agencyId: string, key: string) => `/features/${agencyId}/check/${key}`,
  },

  techficient: {
    products: "/techficient/products",
    quote: "/techficient/quote",
    quotes: "/techficient/quotes",
    myQuotes: "/techficient/quotes/mine",
    pushClient: "/techficient/clients/push",
    initiateEapp: "/techficient/eapp/initiate",
    directSubmitEapp: "/techficient/eapp/direct-submit",
    ensureQuoteLead: (techficientQuoteRequestId: string) =>
      `/techficient/quotes/${techficientQuoteRequestId}/ensure-lead`,
    resumeEapp: (caseId: string) => `/techficient/eapp/resume/${caseId}`,
    cases: "/techficient/cases",
    casesSync: "/techficient/cases/sync",
    caseById: (caseId: string) => `/techficient/cases/${caseId}`,
    eligibility: "/techficient/eligibility",
    myCommissions: "/techficient/commissions/mine",
    commissionForPolicy: (policyId: string) =>
      `/techficient/commissions/policy/${policyId}`,
    agencyDashboard: (agencyId: string) =>
      `/techficient/agency/${agencyId}/dashboard`,
    agentDetails: (userId: string) =>
      `/techficient/agency/agent/${userId}/details`,
    onboard: "/techficient/onboard",
    profile: "/techficient/profile",
  },
};

// Endpoint pattern lists for agency-aware requests
// These are used by `axiosConfig` to decide when to include the `x-agency-id` header
// Keep patterns simple substring matches for robustness
export const AGENCY_ENDPOINTS = [
  "/agencies",                         // Agency management and stats
  "/leads",                            // Lead lists, details, assignments
  "/appointments",                     // Appointments are agency-scoped
  "/lead-assignment",                  // Lead assignment dashboards
  "/dashboard/agency/",                // Agency dashboards
  "/v1/team-chat",                      // Team chat is agency-scoped
  "/transaction/premium-sales/agency", // Premium sales by agency
];

export const EXCLUDED_AGENCY_ENDPOINTS = [
  "/auth/",           // Auth and profile auth endpoints
  "/api/health",      // Health checks
  "/config/",         // Public config
  "/stripe/",         // Payments handled by user scope
  "/subscriptions",   // Subscription management is user/tenant scoped
  "/upload/",         // Upload service
  "/ai/",             // AI endpoints (user scoped with internal checks)
  "/chatbot/",        // Chatbot endpoints
  "/communication/",  // Telephony and messaging (user scoped)
  "/resources",       // Global resources catalog
  "/notifications",   // Notifications are user scoped
  "/fax/",            // Fax compat endpoints
  "/policies",        // Policy endpoints are user scoped
];
